import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isMichromaBrand } from '@/constants/brandPattern';
import { useBrandPattern } from '@/hooks/useBrandPattern';

const navItems = [
  { label: 'home', href: '/' },
  { label: 'company', href: '/company' },
  { label: 'works', href: '/works' },
  { label: 'recruit', href: '/recruit' },
  { label: 'contact', href: '/contact' },
  { label: 'access', href: '/access' },
];

export default function Navbar() {
  const { pattern } = useBrandPattern();
  const michroma = isMichromaBrand(pattern);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const isHome = location.pathname === '/';

  useEffect(() => {
    // ホームページはヒーロー（85vh）を抜けたら白背景に切り替え
    // 他ページはページヘッダー黒背景エリア（280px）を抜けたら白背景に切り替え
    const threshold = isHome ? window.innerHeight * 0.85 : 280;
    const handleScroll = () => setScrolled(window.scrollY > threshold);
    // ページ遷移時に即座に状態を更新
    setScrolled(window.scrollY > threshold);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome, location.pathname]);

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // 全ページ共通：黒背景エリアを抜けるまで透明＋白文字、抜けたら白背景＋黒文字
  const isTransparent = !scrolled;

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        isTransparent
          ? 'bg-transparent'
          : 'bg-white/97 backdrop-blur-sm border-b border-gray-100/80'
      }`}
    >
      <div className="mx-auto px-6 md:px-12 flex items-center justify-between min-h-[4.5rem] md:min-h-[5.5rem] py-2">
        {/* Logo（ヒーローと同じ CADEC 表示用フォント） */}
        <Link
          to="/"
          className="cadec-inline-cq inline-flex w-max max-w-[min(100%,280px)] shrink-0 flex-col items-stretch text-center no-underline transition-transform duration-300 hover:-translate-y-0.5 md:max-w-none"
        >
          {/*
            上段の CADEC 幅に合わせ、下段を同じボックス幅でセンタリングし横に近づくよう配置
          */}
          <span
            className={`cadec-nav-wordmark block font-normal leading-none transition-colors duration-300 ${
              michroma ? 'cadec-nav-wordmark--michroma' : ''
            }`}
            style={{
              fontFamily: pattern.fontFamily,
              fontWeight: pattern.wordmarkWeight,
              letterSpacing: pattern.wordmarkLetterSpacing,
              color: isTransparent ? '#FFFFFF' : '#111111',
            }}
          >
            {pattern.wordmarkChars.join('')}
          </span>
          <span
            className="mt-0.5 block w-full max-w-full leading-tight transition-colors duration-300 [word-break:keep-all]"
            style={{
              fontSize: 'clamp(9px, 0.78vw, 11px)',
              fontFamily: '"Noto Sans JP", sans-serif',
              fontWeight: 500,
              /* 上段（CADEC）幅に近づけるための字間。最後字の外側揃いは padding で相殺 */
              letterSpacing: '0.18em',
              paddingLeft: '0.12em',
              paddingRight: '0.12em',
              color: isTransparent ? '#FFFFFF' : '#9CA3AF',
            }}
          >
            一級建築士事務所
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-9">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.label}
                to={item.href}
                className={[
                  'group relative inline-block cursor-pointer whitespace-nowrap',
                  'text-sm md:text-[15px] tracking-[0.18em] font-medium',
                  'transition-transform duration-300 ease-out hover:-translate-y-0.5',
                  isTransparent
                    ? isActive
                      ? 'text-white'
                      : 'text-white hover:text-white'
                    : isActive
                      ? 'text-gray-900'
                      : 'text-gray-500 hover:text-gray-900',
                ].join(' ')}
                style={{ fontFamily: '"Inter", sans-serif' }}
              >
                {item.label}
                <span
                  className={[
                    'absolute -bottom-1.5 left-0 h-0.5 transition-all duration-300 ease-out',
                    isActive ? 'w-full' : 'w-0 group-hover:w-full',
                  ].join(' ')}
                  style={{ background: isTransparent ? '#FFFFFF' : '#111111' }}
                  aria-hidden
                />
              </Link>
            );
          })}
        </nav>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 cursor-pointer p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="メニュー"
        >
          <span
            className="block w-5 h-px transition-all duration-300"
            style={{
              background: isTransparent ? '#FFFFFF' : '#111111',
              transform: menuOpen ? 'rotate(45deg) translateY(8px)' : 'none',
            }}
          />
          <span
            className="block w-5 h-px transition-all duration-300"
            style={{
              background: isTransparent ? '#FFFFFF' : '#111111',
              opacity: menuOpen ? 0 : 1,
            }}
          />
          <span
            className="block w-5 h-px transition-all duration-300"
            style={{
              background: isTransparent ? '#FFFFFF' : '#111111',
              transform: menuOpen ? 'rotate(-45deg) translateY(-8px)' : 'none',
            }}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className="md:hidden bg-white overflow-hidden transition-all duration-300"
        style={{ maxHeight: menuOpen ? '400px' : '0', opacity: menuOpen ? 1 : 0, borderTop: menuOpen ? '1px solid #F3F4F6' : 'none' }}
      >
        <nav className="flex flex-col px-6 py-6 gap-5">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.label}
                to={item.href}
                className="text-[15px] tracking-[0.2em] font-medium transition-all duration-200 cursor-pointer whitespace-nowrap py-1.5 pl-0 hover:pl-1.5 hover:-translate-y-0.5 hover:text-gray-900"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: isActive ? '#111111' : '#9CA3AF',
                }}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
      </div>
    </header>
  );
}
